import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, StatusBadge, inputClass, timeAgo, useAdminApi, useToast } from './ui';
import { HEALTH_COLORS, HEALTH_LABELS, HEALTH_PRIORITY, getHealth } from './health';
import IntegrationDrawer from './IntegrationDrawer';
import AddIntegrationModal from './AddIntegrationModal';

const HEALTH_FILTERS = [
  ['all', 'All health'],
  ['attention', 'Needs attention'],
  ['failing', 'Failing'],
  ['stale', 'Stale'],
  ['new', 'New (awaiting approval)'],
  ['running', 'Running'],
  ['ok', 'Healthy'],
  ['paused', 'Paused / rejected'],
];

const SORTS = [
  ['attention', 'Problems first'],
  ['lastRun', 'Last run (newest)'],
  ['place', 'Place A–Z'],
  ['created', 'Recently added'],
];

function AdminIntegrations({ data, reload, preset, openUuid, onOpened }) {
  const api = useAdminApi();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [health, setHealth] = useState('all');
  const [status, setStatus] = useState('all');
  const [region, setRegion] = useState('all');
  const [sort, setSort] = useState('attention');
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyUuid, setBusyUuid] = useState(null);

  // Filters/drawer requested from other tabs (e.g. clicking "Failing" on the overview)
  useEffect(() => {
    if (preset && preset.health) {
      setHealth(preset.health);
      setStatus('all');
      setSearch('');
    }
  }, [preset]);

  useEffect(() => {
    if (openUuid) {
      setSelectedUuid(openUuid);
      onOpened && onOpened();
    }
  }, [openUuid, onOpened]);

  const rows = useMemo(
    () => data.placeIntegrations.map((integration) => ({ integration, health: getHealth(integration) })),
    [data.placeIntegrations]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter(({ integration, health: h }) => {
      if (q) {
        const haystack = [integration.place.name, integration.place.region.name, integration.name, integration.directory, integration.integrationType.name, integration.url]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (status !== 'all' && integration.status !== status) return false;
      if (region !== 'all' && String(integration.place.region.id) !== region) return false;
      if (health === 'attention') return ['failing', 'stale', 'new'].includes(h.key);
      if (health !== 'all' && h.key !== health) return false;
      return true;
    });

    const lastRunTime = (h) => (h.lastRun ? new Date(h.lastRun.createdAt).getTime() : 0);
    filtered.sort((a, b) => {
      if (sort === 'lastRun') return lastRunTime(b.health) - lastRunTime(a.health);
      if (sort === 'created') return new Date(b.integration.createdAt) - new Date(a.integration.createdAt);
      if (sort === 'place') return a.integration.place.name.localeCompare(b.integration.place.name);
      return HEALTH_PRIORITY[a.health.key] - HEALTH_PRIORITY[b.health.key]
        || a.integration.place.name.localeCompare(b.integration.place.name);
    });
    return filtered;
  }, [rows, search, status, region, health, sort]);

  const selected = data.placeIntegrations.find((integration) => integration.uuid === selectedUuid) || null;

  const queueRun = async (integration) => {
    setBusyUuid(integration.uuid);
    try {
      await api(`/admin/integration/${integration.uuid}/queue-run`, { method: 'POST' });
      toast(`Queued ${integration.name || integration.place.name}. Refreshing in a moment…`);
      setTimeout(() => reload({ silent: true }), 15000);
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusyUuid(null);
    }
  };

  const filtersActive = search || health !== 'all' || status !== 'all' || region !== 'all';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search place, region, name or directory…"
          className={`${inputClass} !w-72`}
        />
        <select value={health} onChange={(e) => setHealth(e.target.value)} className={`${inputClass} !w-auto`}>
          {HEALTH_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputClass} !w-auto`}>
          <option value="all">Any status</option>
          {['new', 'active', 'review', 'paused', 'rejected'].map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={`${inputClass} !w-auto`}>
          <option value="all">All regions</option>
          {data.regions.map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={`${inputClass} !w-auto`}>
          {SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {filtersActive && (
          <button className="text-sm underline text-gray-600" onClick={() => { setSearch(''); setHealth('all'); setStatus('all'); setRegion('all'); }}>
            Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-500">{visible.length} of {rows.length}</span>
          <Button variant="primary" onClick={() => setAdding(true)}>+ Add integration</Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Place</th>
              <th className="px-4 py-2 font-medium">Integration</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Health</th>
              <th className="px-4 py-2 font-medium">Last run</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-500">No integrations match these filters.</td></tr>
            )}
            {visible.map(({ integration, health: h }) => (
              <tr
                key={integration.uuid}
                onClick={() => setSelectedUuid(integration.uuid)}
                className={`cursor-pointer hover:bg-gray-50 ${selectedUuid === integration.uuid ? 'bg-yellow-50' : ''}`}
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/region/${integration.place.region.urlName}/place/${integration.place.urlName}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium hover:underline"
                  >
                    {integration.place.name}
                  </Link>
                  <div className="text-xs text-gray-500">{integration.place.region.name}</div>
                </td>
                <td className="px-4 py-3">
                  <div>{integration.name || '—'}</div>
                  <div className="text-xs text-gray-500">
                    {integration.integrationType.name}
                    {integration.directory ? ` · ${integration.directory}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={integration.status} />
                  <div className="text-xs text-gray-500 mt-1">{integration.action || '—'} · {integration.frequency || 'no schedule'}</div>
                </td>
                <td className="px-4 py-3">
                  <Badge color={HEALTH_COLORS[h.key]}>{HEALTH_LABELS[h.key]}</Badge>
                  {h.reasons.length > 0 && h.key !== 'ok' && <div className="text-xs text-gray-500 mt-1 max-w-[16rem]">{h.reasons.join(' · ')}</div>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {h.lastRun ? (
                    <>
                      <div>{timeAgo(h.lastRun.createdAt)}</div>
                      <div className="text-xs text-gray-500">
                        {h.lastRun.added != null || h.lastRun.updated != null ? `+${h.lastRun.added || 0} / ~${h.lastRun.updated || 0}` : h.lastRun.status}
                      </div>
                    </>
                  ) : <span className="text-gray-400">never</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button busy={busyUuid === integration.uuid} onClick={() => queueRun(integration)}>Run</Button>
                  <Button className="ml-2" onClick={() => setSelectedUuid(integration.uuid)}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <IntegrationDrawer
          key={selected.uuid}
          integration={selected}
          data={data}
          reload={reload}
          onClose={() => setSelectedUuid(null)}
        />
      )}

      {adding && (
        <AddIntegrationModal
          data={data}
          reload={reload}
          onClose={() => setAdding(false)}
          onCreated={(integration) => { setAdding(false); setSelectedUuid(integration.uuid); }}
        />
      )}
    </div>
  );
}

export default AdminIntegrations;
